/**
 * Editable onboarding email templates (T-098 post-signature pair; T-129 added
 * the magic-link invite).
 *
 * One renderer, imported by BOTH the API (at send time) and the web Settings
 * preview — so the preview is byte-identical to what is emailed. The body is
 * markdown rendered with markdown-it `html: false` (raw HTML is escaped, not
 * executed). Placeholders are substituted AFTER rendering, into the HTML, with
 * HTML-escaped values — so dynamic data can never inject markdown or markup.
 *
 * The invite email's one-time-link button + expiry note are STRUCTURAL —
 * appended by renderInviteEmail after the editable body — so editing the
 * template can never break the onboarding link.
 */
import MarkdownIt from 'markdown-it';

export const ONBOARDING_EMAIL_KEYS = ['invite', 'post_sig_1', 'post_sig_2'] as const;
export type OnboardingEmailKey = (typeof ONBOARDING_EMAIL_KEYS)[number];

export interface OnboardingEmailTemplate {
  subject: string;
  /** Body authored in markdown. */
  body: string;
}

export interface OnboardingEmailMeta extends OnboardingEmailTemplate {
  label: string;
  description: string;
  /** Placeholder names valid in this email's subject + body. */
  placeholders: readonly string[];
}

export const ONBOARDING_EMAIL_DEFAULTS: Record<OnboardingEmailKey, OnboardingEmailMeta> = {
  invite: {
    label: 'Magic-link invite',
    description:
      'Sent when an onboarding is created — the one-time onboarding-form link (button + expiry note) is appended automatically below the body and cannot be edited away.',
    placeholders: ['resourceName', 'clientName'],
    subject: 'Complete your onboarding for {{clientName}}',
    body: `Hi {{resourceName}},

ultimumIT has started your onboarding for **{{clientName}}**. Click the button below to provide your details and documents.

## Important information for onboarding

**Insurance:** Subcontractor agrees to maintain the following minimum insurance coverage during the term of this Agreement: (i) worker's compensation insurance or employer's liability insurance, as required by applicable law; (ii) comprehensive Errors and Omissions insurance with limits of $2,000,000 combined single limit; (iii) comprehensive commercial liability and property damage insurance with bodily injury and property damage limits of at least $2,000,000 combined single limit. Certificates evidencing such insurance shall be furnished to ultimumIT upon request.

The liability of the subcontractor shall not/in no manner exceed the amount of the sum assured by the policies mentioned under this agreement. This liability includes reasonable attorney's fee, court costs, other legal fees and expenses of any kind related to this agreement.

ultimumIT Inc.
231 Market PL, STE 260
San Ramon, CA 94583`,
  },
  post_sig_1: {
    label: 'Email 1 · Security Policies',
    description:
      'Sent first once the contract is fully signed. The active policy documents are attached automatically.',
    placeholders: ['contractorName', 'signatoryName'],
    subject: 'ultimumIT — Security Policies & Engagement Guidelines',
    body: `Hello {{contractorName}}

Attached are ultimumIT's security policies.

Please review them carefully before your first working day on-site or accessing any of the client's systems.

## Project Engagement Guidelines

To ensure the success of this project, all team members are expected to adhere to the following professional standards:

- **Capacity Management:** If you reach capacity before 40 hours, notify your manager immediately to request additional tasks. If you require additional hours over 40, prior coordination with your line manager is required. In times of heavy workload, please request and obtain approval and bill total hours.
- **Focus:** This engagement requires your full professional attention. Please dedicate your time solely to this project's objectives.
- **Prioritization:** Work must be completed according to the priorities established by your manager. Please align with them before adjusting your task list.
- **Availability:** As a remote team, being responsive on chat during core business hours is essential. If you need to step away for an extended period, notify your manager or a team member and coordinate how your time will be balanced.
- **System Access:** Promptly report any access issues to your manager so they can facilitate a resolution. Stay proactive and provide updates until access is granted.
- **Time Off:** Please coordinate all time-off requests with your manager in writing as far in advance as possible to ensure adequate coverage.

By continuing to work on this project, you acknowledge that you have read, understood, and will comply with these policies and guidelines.

Thank you for your cooperation in helping us protect both our company's and our client's information and policies.

Regards,
{{signatoryName}}`,
  },
  post_sig_2: {
    label: 'Email 2 · Engagement details',
    description: 'Sent after Email 1 — confirms the engagement start date.',
    placeholders: ['contractorName', 'signatoryName', 'startDate'],
    subject: 'ultimumIT — Engagement details & start date',
    body: `Hi {{contractorName}},

Your engagement is confirmed with a start date of **{{startDate}}**. We'll follow up with any remaining details.

Regards,
{{signatoryName}}`,
  },
};

const md = new MarkdownIt({ html: false, breaks: true, linkify: false });

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function substituteHtml(html: string, vars: Record<string, string>): string {
  return html.replace(/\{\{(\w+)\}\}/g, (m, k: string) => (k in vars ? escapeHtml(vars[k] ?? '') : m));
}

function substituteText(text: string, vars: Record<string, string>): string {
  return text.replace(/\{\{(\w+)\}\}/g, (m, k: string) => (k in vars ? (vars[k] ?? '') : m));
}

function wrapEmailHtml(inner: string): string {
  return `<div style="font-family:ui-sans-serif,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.55;color:#18181b;max-width:560px">${inner}</div>`;
}

/** Render a markdown body to the wrapped email HTML, substituting placeholders. */
export function renderEmailBodyHtml(bodyMarkdown: string, vars: Record<string, string>): string {
  return wrapEmailHtml(substituteHtml(md.render(bodyMarkdown), vars));
}

/** Render a full onboarding email (subject + body) for the given variables. */
export function renderOnboardingEmail(
  template: OnboardingEmailTemplate,
  vars: Record<string, string>,
): { subject: string; html: string } {
  return {
    subject: substituteText(template.subject, vars),
    html: renderEmailBodyHtml(template.body, vars),
  };
}

/** The structural one-time-link block (button + paste fallback + expiry note). */
export function inviteLinkHtml(inviteUrl: string): string {
  const url = escapeHtml(inviteUrl);
  return (
    `<p><a href="${url}" style="display:inline-block;background:#2563eb;color:#fff;text-decoration:none;padding:10px 18px;border-radius:6px;font-weight:600">Start onboarding</a></p>` +
    `<p style="color:#52525b;font-size:13px">Or paste this link: ${url}</p>` +
    `<p style="color:#52525b;font-size:13px">This is a one-time link — it expires once you submit.</p>`
  );
}

/**
 * Render the magic-link invite email: the editable markdown body, then the
 * STRUCTURAL link block — appended outside the template so an edit can never
 * remove the onboarding link (T-129).
 */
export function renderInviteEmail(
  template: OnboardingEmailTemplate,
  vars: { resourceName: string; clientName: string },
  inviteUrl: string,
): { subject: string; html: string } {
  const inner = substituteHtml(md.render(template.body), vars);
  return {
    subject: substituteText(template.subject, vars),
    html: wrapEmailHtml(inner + inviteLinkHtml(inviteUrl)),
  };
}
